import * as React from 'react';

import classNames from 'classnames';

import {CheckmarkButton} from '../button/checkmark-button/checkmark-button';
import {CloseButton} from '../button/close-button/close-button';
import {TextField} from '../textfield/textfield';
import {Body} from '../typography/body';
import {LinkifyTextCustomField} from '../utils/linkify-text-custom-field';

type Props = {
    value: string | null | undefined;
    displayValue?: string | null | undefined;
    onUpdate: (arg1?: string | null | undefined) => void;
    isMultiline?: boolean;
    placeholder?: string;
    preventHideOnBlur?: boolean;
    isEditing: boolean;
    setIsEditing: (arg1: boolean) => void;
    shouldLinkify?: boolean;
    noTopMargin?: boolean;
    ignorePasswordAutoFill?: boolean;
};

export function EditableTextfieldContent(props: Props) {
    const {value, displayValue} = props;
    const startingValue = value || '';

    const [internalValue, setInternalValue] = React.useState<string>(startingValue);

    // We also need to store the value in a reference so that closures (such as
    // `saveAndHide`) have access to the latest value, even if they were
    // rendered before the state was updated
    const valueRef = React.useRef(value);

    // Sync internal value when the prop value changes (ex. after a save)
    // OR when editing begins (handles cases where only end whitespace was changed)
    React.useEffect(() => {
        setValue(startingValue);
    }, [startingValue, props.isEditing]);

    const setValue = (newValue: string) => {
        setInternalValue(newValue);
        valueRef.current = newValue;
    };

    // @ts-expect-error
    const saveAndHide = (e) => {
        e.stopPropagation();

        // If the value has changed, save it, otherwise just hide the textfield
        if (valueRef.current !== startingValue) {
            props.onUpdate(valueRef.current ? valueRef.current.trim() : '');
        } else {
            props.setIsEditing(false);
        }
    };

    // @ts-expect-error
    const handleEscape = (e) => {
        e.stopPropagation();

        setValue(startingValue);
        props.setIsEditing(false);
    };

    const editClassNames = classNames('flex gap-4 word-break full-width min-width-0', {
        'mt-4': !props.noTopMargin,
        'align-center': !props.isMultiline,
        'align-start': props.isMultiline,
    });

    const valueToRender = displayValue || value;

    return (
        <div
            className='flex flex-dir-col full-width'
            onClick={(e) => (props.isEditing ? e.stopPropagation() : undefined)}
        >
            {props.isEditing ? (
                <div className={editClassNames}>
                    <div className='flex-grow min-width-0'>
                        <TextField
                            placeholder={props.placeholder || 'Add a note…'}
                            size='small'
                            value={internalValue}
                            onChange={(newValue) => {
                                setValue(newValue);
                            }}
                            // If the note is multiline, we want to allow adding
                            // newlines
                            onEnter={props.isMultiline ? undefined : saveAndHide}
                            onEscape={handleEscape}
                            // @ts-expect-error
                            onBlur={props.preventHideOnBlur ? undefined : saveAndHide}
                            focusOnMount={true}
                            multiline={props.isMultiline}
                            shouldGrowVertically={true}
                            ignorePasswordAutoFill={props.ignorePasswordAutoFill}
                        />
                    </div>
                    <div className='flex flex-dir-col align-center gap-2'>
                        <CheckmarkButton
                            className='no-padding'
                            onClick={saveAndHide}
                            // @ts-expect-error
                            variant='dark'
                        />
                        <CloseButton size={12} variant='dark' onPointerDown={handleEscape} />
                    </div>
                </div>
            ) : (
                <Body
                    size='small'
                    color={valueToRender ? 'medium' : 'light'}
                    maintainLineBreaks={true}
                    wrapAnywhere={true}
                >
                    {props.value && props.shouldLinkify ? (
                        <LinkifyTextCustomField
                            text={props.value}
                            displayText={props.displayValue ?? undefined}
                        />
                    ) : (
                        valueToRender || props.placeholder
                    )}
                </Body>
            )}
        </div>
    );
}
